import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useListings } from '../hooks/useListings';
import { useAllUsers } from '../hooks/useUsers';
import { pendingQueue } from '../utils/superAdmin';
import SuperDirectoryTab from '../components/admin/super/SuperDirectoryTab';
import ApprovalsTab from '../components/admin/super/ApprovalsTab';
import PeopleTab from '../components/admin/super/PeopleTab';
import MessagesTab from '../components/admin/super/MessagesTab';
import { useContactMessages } from '../hooks/useContactMessages';

type SuperTab = 'approvals' | 'directory' | 'messages' | 'people';
const TABS: SuperTab[] = ['approvals', 'directory', 'messages', 'people'];

/** Super admin panel (plan 11.2). The open tab is in the URL as ?tab=. */
export default function SuperAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { listings, loading, error } = useListings({ includeUnpublished: true });
  const { users } = useAllUsers();

  const requested = searchParams.get('tab') as SuperTab | null;
  const tab: SuperTab = requested && TABS.includes(requested) ? requested : 'directory';
  const pendingCount = pendingQueue(listings).length;
  const contact = useContactMessages();
  const openMessages = contact.messages.filter((m) => !m.done).length;
  const badge = (id: SuperTab) => (id === 'approvals' ? pendingCount : id === 'messages' ? openMessages : 0);

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <h1 className="text-lg font-semibold text-ink">{t('admin.superAdminTitle')}</h1>
          {user?.email && <p className="text-sm text-ash truncate">{user.email}</p>}
        </div>
        <button type="button" onClick={handleLogout} className="text-sm text-ink hover:underline min-h-[44px]">
          {t('admin.logout')}
        </button>
      </div>

      <div role="tablist" className="no-scrollbar -mx-4 px-4 flex gap-2 overflow-x-auto border-b border-ink/10">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setSearchParams({ tab: id })}
            className={`shrink-0 whitespace-nowrap min-h-[44px] px-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === id ? 'border-ink text-ink font-semibold' : 'border-transparent text-ash hover:text-ink'
            }`}
          >
            {t(`directory.super.tab.${id}`)}
            {badge(id) > 0 && (
              <span className="ms-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-ink text-paper text-xs tabular-nums">
                {badge(id)}
              </span>
            )}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {loading ? (
          <p className="text-sm text-ash">{t('common.loading')}</p>
        ) : error ? (
          <p className="text-sm text-danger-600">{error}</p>
        ) : tab === 'approvals' ? (
          <ApprovalsTab listings={listings} users={users} />
        ) : tab === 'messages' ? (
          <MessagesTab state={contact} />
        ) : tab === 'people' ? (
          <PeopleTab listings={listings} users={users} />
        ) : (
          <SuperDirectoryTab listings={listings} users={users} />
        )}
      </div>
    </div>
  );
}
